import { applyDomainLimits } from '../domain/limits'
import { applyBrandMapColors } from '../brand/mapColors'
import { basemapFrom } from '../map/basemap'
import { applyBasemap } from '../map/baseStyle'
import { refreshBasemaps } from '../map/basemapRefresh'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import {
  applyLang,
  getLang,
  navigatorLangs,
  setLang as persistLang,
  storedLang,
  subscribeLang,
  t as ambientT,
} from './store'
import { resolveLang, showSwitcher, siteLangs, type SiteBrand, type SiteLangs } from './resolveLang'
import { SITE_WAIT_MS, cacheSite, holdFirstPaint, parseBrand, readCachedSite } from './siteCache'
import { isLang, type Lang, type Vars } from './types'

/**
 * Язык живёт НАД маршрутами и НАД ViewContext.
 *
 * ViewContext держит то, что переживает переход между разделами — дату, глубину,
 * вид, регион карты. Язык туда не складывается: он нужен раньше маршрутов (экран
 * входа, границы ошибок) и меняется независимо от вида. Смена языка не должна
 * ронять запомненный регион карты — именно поэтому уровни разные.
 */
interface LangContextValue {
  lang: Lang
  /** Набор языков домена (пусто — хост ещё не ответил). */
  langs: Lang[]
  /** Показывать ли переключатель: только когда есть из чего выбирать. */
  canSwitch: boolean
  /**
   * Бренд домена (null — пак не доехал; имя и знак берутся запасные).
   *
   * Живёт здесь, а не в своём провайдере, потому что приезжает тем же ответом
   * `/site` и тем же кэшем: второй провайдер означал бы второй запрос до
   * первого кадра и вторую дорожку, которая рассинхронится с этой.
   */
  brand: SiteBrand | null
  setLang: (lang: Lang) => void
  /** Профиль вошедшего пользователя: `users.ui_lang` (null — не выбирал). */
  adoptProfileLang: (lang: string | null | undefined) => void
  t: (key: string, vars?: Vars) => string
}

const Ctx = createContext<LangContextValue | null>(null)

const SITE_URL = '/api/v1/cabinet/site'
const PROFILE_LANG_URL = '/api/v1/cabinet/profile/lang'

/** Что домен разрешает на этом хосте. Без авторизации — нужно экрану входа. */
async function fetchSite(): Promise<SiteLangs | null> {
  try {
    const res = await fetch(SITE_URL)
    if (!res.ok) return null
    const body = (await res.json()) as Partial<SiteLangs>
    if (!Array.isArray(body?.languages) || typeof body?.default_lang !== 'string') return null
    // Бренд переносим явно, как и языки: ответ ручки пересобирается по полям, и
    // забытое поле здесь означало бы бренд, который приехал и потерялся молча.
    return {
      languages: body.languages,
      default_lang: body.default_lang,
      brand: parseBrand(body.brand),
    }
  } catch {
    return null
  }
}

export function LangProvider({
  children,
  /** Только для demo-shell и тестов: минуя сеть. */
  site: siteOverride,
}: {
  children: ReactNode
  site?: SiteLangs | null
}) {
  const lang = useSyncExternalStore(subscribeLang, getLang, getLang)
  // Набор домена берём из памяти origin ещё до сети: на английском домене
  // localStorage свой и пустой, поэтому раньше КАЖДАЯ жёсткая перезагрузка
  // показывала русский кабинет и переписывала его английским на глазах
  // (владелец, 12.08.2026). См. siteCache.
  const [site, setSite] = useState<SiteLangs | null>(siteOverride ?? readCachedSite())
  const [profileLang, setProfileLang] = useState<string | null>(null)
  // Терпение первого захода: набора нет, ждём ответ ручки и не рисуем наугад.
  const [waited, setWaited] = useState(siteOverride !== undefined)

  // Набор домена приезжает одним лёгким запросом; ответ запоминаем на origin.
  useEffect(() => {
    if (siteOverride !== undefined) return
    let cancelled = false
    const giveUp = setTimeout(() => {
      if (!cancelled) setWaited(true)
    }, SITE_WAIT_MS)
    fetchSite().then(loaded => {
      if (cancelled) return
      cacheSite(loaded)
      setSite(loaded)
      setWaited(true)
    })
    return () => {
      cancelled = true
      clearTimeout(giveUp)
    }
  }, [siteOverride])

  useEffect(() => {
    applyResolved(site, profileLang)
  }, [site, profileLang])

  const setLang = useCallback(
    (next: Lang) => {
      // Домен главнее: язык вне набора не применяем даже по прямому вызову.
      const allowed = siteLangs(site)
      if (allowed.length && !allowed.includes(next)) return
      persistLang(next)
      setProfileLang(next)
      // Профиль догоняет в фоне: переключение мгновенное, каталог уже в бандле.
      void saveProfileLang(next)
    },
    [site],
  )

  // Один перенос выбора в профиль на сессию: без этого каждый рефреш повторял
  // бы PUT, а вошедший пользователь с пустым ui_lang получал бы его снова.
  const migrated = useRef(false)
  const adoptProfileLang = useCallback((value: string | null | undefined) => {
    if (isLang(value)) {
      setProfileLang(value)
      return
    }
    // Профиль пуст: если до входа язык выбирали — перенести выбор в профиль.
    const stored = storedLang()
    setProfileLang(stored)
    if (stored && !migrated.current) {
      migrated.current = true
      void saveProfileLang(stored)
    }
  }, [])

  // Гамма линий карты — в модульный регистр: слои MapLibre строит императивный
  // код, а шевроны и подписи рисуются в canvas 2D, и контекста React там нет.
  // Тем же приёмом здесь живёт язык (`setLang`).
  applyBrandMapColors(site?.brand ?? null)
  // Рамка и окно дат домена — тем же рельсом: карта строится с `maxBounds`
  // с первого кадра, без кадра чужой воды (решение владельца 20.10.2026).
  applyDomainLimits({ frame: site?.brand?.frame ?? null, period: site?.brand?.period ?? null })

  // Подложка — тем же рельсом и ИМЕННО ЗДЕСЬ, до первого кадра. Карта строится
  // синхронно (`new maplibregl.Map({style: baseStyle()})`), а `siteCache`
  // специально читает ответ домена до отрисовки — тем же движением, которым
  // закрыта вспышка русского интерфейса на английском домене (12.08.2026).
  // Применив подложку позже, мы получили бы карту, моргающую чужой
  // картографией.
  //
  // Язык здесь не украшение: у Яндекса он стоит в АДРЕСЕ тайла, и переключение
  // языка обязано менять подписи на карте (`refreshBasemapSource` довозит это
  // до уже созданных карт).
  const basemap = basemapFrom(site?.brand?.basemap ?? null, lang)
  applyBasemap(basemap)
  // Карты, уже стоящие на экране, своих адресов сами не пересматривают: реестр
  // довозит новый до каждой. Молчит, когда адрес тот же, — лишний `setTiles`
  // роняет кэш плиток и заставляет перекачать видимый кадр.
  refreshBasemaps(basemap)

  const value = useMemo<LangContextValue>(
    () => ({
      lang,
      langs: siteLangs(site),
      canSwitch: showSwitcher(site),
      brand: site?.brand ?? null,
      setLang,
      adoptProfileLang,
      t: ambientT,
    }),
    [lang, site, setLang, adoptProfileLang],
  )

  // Первый в жизни заход на домен: пока набор неизвестен, показывать нечего —
  // любой текст был бы догадкой. Ручка локальная и лёгкая, окно — десятки
  // миллисекунд; молчащий бэкенд разблокирует таймер (см. holdFirstPaint).
  if (holdFirstPaint(site, waited)) return null

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * Пересчитать язык по набору домена и ПРИМЕНИТЬ его (не запомнить).
 *
 * Применить язык домена и запомнить выбор пользователя — разные события: домен,
 * разрешающий один язык, не должен переписывать localStorage и профиль, иначе
 * возврат на rybprognoz.ru не вернул бы русский.
 */
function applyResolved(site: SiteLangs | null, profileLang: string | null): void {
  applyLang(
    resolveLang({
      site,
      profileLang,
      storedLang: storedLang(),
      browserLangs: navigatorLangs(),
    }),
  )
}

/** Записать язык в профиль. Молча: отказ сети не должен мешать переключению. */
async function saveProfileLang(lang: Lang): Promise<void> {
  try {
    const token = localStorage.getItem('cabinet.token')
    if (!token) return
    await fetch(PROFILE_LANG_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ lang }),
    })
  } catch {
    /* ignore */
  }
}

export function useLang(): LangContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useLang must be used within LangProvider')
  return ctx
}

/**
 * Бренд домена, без требования провайдера.
 *
 * Отдельным хуком, а не через `useLang()`, потому что знак рисуется и там, где
 * провайдера может не оказаться (внешняя граница ошибок, визуальные харнессы):
 * падение из-за отсутствующего контекста стоило бы дороже, чем запасной знак.
 */
export function useSiteBrand(): SiteBrand | null {
  return useContext(Ctx)?.brand ?? null
}

/** Только функция перевода — самый частый случай в компонентах. */
export function useT(): (key: string, vars?: Vars) => string {
  const lang = useSyncExternalStore(subscribeLang, getLang, getLang)
  // lang в зависимостях намеренно: смена языка обязана перерисовать компонент.
  return useMemo(() => (key: string, vars?: Vars) => ambientT(key, vars), [lang])
}
